
import { motion, useMotionValue, useTransform, PanInfo, useAnimation } from 'framer-motion';
import { ReactNode, useEffect } from 'react';
import { CheckCircle2 } from 'lucide-react';

interface Props {
    children: ReactNode;
    onSwipeComplete: () => void;
    enabled?: boolean;
    confirmMessage?: string;
}

export function SwipeableOrderCard({ children, onSwipeComplete, enabled = true, confirmMessage }: Props) {
    const controls = useAnimation();
    const x = useMotionValue(0);

    // Opacity of the background icon/text based on drag distance
    const iconOpacity = useTransform(x, [-50, -100], [0, 1]);
    const iconScale = useTransform(x, [-50, -100], [0.5, 1.2]);

    async function handleDragEnd(_event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) {
        const offset = info.offset.x;
        const velocity = info.velocity.x;

        // Trigger if dragged far enough left
        if (offset < -120 || (offset < -50 && velocity < -500)) {
            if (confirmMessage) {
                if (!window.confirm(confirmMessage)) {
                    controls.start({ x: 0 });
                    return;
                }
            }

            // Animate off screen to the left? Or just trigger.
            // For this use case (marking as done), usually the item disappears from the "Ready" list.
            // So animating it out is good feedback.
            await controls.start({ x: -500, opacity: 0 });
            onSwipeComplete();
        } else {
            // Snap back
            controls.start({ x: 0 });
        }
    }

    // Reset position if disabled changes or external updates
    useEffect(() => {
        if (!enabled) {
            controls.start({ x: 0 });
        }
    }, [enabled, controls]);

    if (!enabled) {
        return <div className="relative">{children}</div>;
    }

    return (
        <div className="relative overflow-hidden rounded-lg">
            {/* Background Layer (Revealed on swipe) */}
            <div className="absolute inset-0 bg-green-600 rounded-lg flex items-center justify-end pr-8">
                <motion.div style={{ opacity: iconOpacity, scale: iconScale }} className="flex items-center gap-2 text-white font-bold">
                    <CheckCircle2 size={24} />
                    <span>Récupérée</span>
                </motion.div>
            </div>

            {/* Foreground Swipeable Card */}
            <motion.div
                drag="x"
                dragConstraints={{ left: 0, right: 0 }}
                dragElastic={{ left: 0.7, right: 0.1 }}
                onDragEnd={handleDragEnd}
                animate={controls}
                style={{ x, touchAction: "pan-y" }} // Important for scrolling vertical list while swiping horizontal
                className="relative bg-white rounded-lg z-10"
            >
                {children}
            </motion.div>
        </div>
    );
}
